import React, { useEffect, useState } from 'react';
import { LinearProgress, linearProgressClasses, styled } from '@mui/material';
import { Colors } from 'library';

interface ProgressBarProps {
    value: number;
    bufferValue?: number;
    max?: number;
    height?: number;
    color?: string;
    bufferColor?: string;
    backgroundColor?: string;
    outlineColor?: string;
}

export function ProgressBar({
    value,
    bufferValue,
    max = 100,
    height = 10,
    color = Colors.secondary,
    bufferColor = Colors.secondaryMuted,
    backgroundColor = Colors.fill,
    outlineColor = 'none',
}: ProgressBarProps) {
    const buffered = bufferValue !== undefined;
    const clamped = Math.max(0, Math.min(value, max));
    const percent = max > 0 ? (clamped / max) * 100 : 0;
    const clampedBuffer = Math.max(clamped, Math.min(bufferValue ?? 0, max));
    const bufferPercent = max > 0 ? (clampedBuffer / max) * 100 : 0;

    const [displayValue, setDisplayValue] = useState(0);
    const [displayBuffer, setDisplayBuffer] = useState(0);
    const outline = outlineColor !== 'none' ? `1px solid ${outlineColor}` : 'none';

    useEffect(() => {
        const frame = requestAnimationFrame(() => {
            setDisplayValue(percent);
            setDisplayBuffer(bufferPercent);
        });
        return () => cancelAnimationFrame(frame);
    }, [percent, bufferPercent]);

    return (
        <StyledLinearProgress
            variant={buffered ? 'buffer' : 'determinate'}
            value={displayValue}
            valueBuffer={displayBuffer}
            barHeight={height}
            barColor={color}
            bufferColor={bufferColor}
            backgroundColor={backgroundColor}
            outline={outline}
        />
    );
}

const StyledLinearProgress = styled(LinearProgress, {
    shouldForwardProp: (prop) =>
        prop !== 'barHeight' && prop !== 'barColor' && prop !== 'bufferColor' && prop !== 'backgroundColor',
})<{ barHeight: number; barColor: string; bufferColor: string; backgroundColor: string; outline: string }>(
    ({ barHeight, barColor, bufferColor, backgroundColor, outline }) => ({
        flex: 1,
        height: barHeight,
        borderRadius: 999,
        backgroundColor: backgroundColor,
        border: outline,
        [`& .${linearProgressClasses.bar}`]: {
            borderRadius: 999,
            backgroundColor: barColor,
            transition: 'transform 800ms cubic-bezier(0.4, 0, 0.2, 1)',
        },
        [`& .${linearProgressClasses.bar2Buffer}`]: {
            backgroundColor: bufferColor,
            backgroundImage: `repeating-linear-gradient(-45deg, ${Colors.white}00 0 3px, ${Colors.white}99 3px 6px)`,
        },
        [`& .${linearProgressClasses.dashed}`]: {
            display: 'none',
        },
    }),
);
